import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Checkbox, Divider, Form, Input, Modal, Popconfirm, Radio, Select, Spin, Switch, Tooltip, message } from "antd";
import { DeleteOutlined, DragOutlined, EditOutlined, LockOutlined, PlusOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Service from "../../service";
import { getResource } from "../../util/getResourcePermission";
import config from "../../permission/config.json";

const { TextArea } = Input;

const FIELD_TYPE_LABELS = {
  text: "Short text",
  textarea: "Long text",
  number: "Number",
  email: "Email",
  phone: "Phone number",
  url: "Link (URL)",
  select: "Dropdown",
  radio: "Radio buttons",
  multiselect: "Multi-select",
  checkbox: "Checkbox (yes/no)",
  date: "Date",
  time: "Time",
  datetime: "Date and time",
};
const OPTION_TYPES = ["select", "radio", "multiselect"];
const FIELD_CARD_STYLE = { width: "calc(33.333% - 16px)", minWidth: "280px", flex: "0 0 auto", display: "flex", flexDirection: "column" };
const SECTION_TITLE_STYLE = { color: "#03497a", fontSize: 18, fontWeight: 600, margin: 0, display: "flex", alignItems: "center", gap: 8 };
const MUTED_NOTE = { fontSize: 12, color: "#999", fontWeight: 400 };

const newId = (prefix) => `${prefix}${Math.random().toString(36).slice(2, 10).padEnd(8, "x")}`;
const fieldsDroppableId = (sectionKey) => `fields:${sectionKey}`;

const LoadingOverlay = ({ loading, children }) => (
  <div style={{ position: "relative" }}>
    {loading && (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Spin size="large" />
      </div>
    )}
    {children}
  </div>
);

/** Disabled preview of how a field will look on the public form. */
const renderPreview = (field) => {
  const common = { placeholder: field.placeholder, disabled: true, style: { width: "100%" } };
  const options = (field.options || []).map((option) => ({ value: option, label: option }));
  switch (field.type) {
    case "textarea":
      return <TextArea {...common} rows={1} />;
    case "number":
      return <Input {...common} type="number" />;
    case "phone":
      return <Input {...common} type="tel" />;
    case "email":
      return <Input {...common} type="email" />;
    case "url":
      return <Input {...common} type="url" />;
    case "date":
      return <Input {...common} type="date" />;
    case "time":
      return <Input {...common} type="time" />;
    case "datetime":
      return <Input {...common} type="datetime-local" />;
    case "file":
      return <Input type="file" disabled style={{ width: "100%" }} />;
    case "select":
      return <Select {...common} options={options} />;
    case "multiselect":
      return <Select {...common} mode="multiple" options={options} />;
    case "radio":
      return (
        <Radio.Group disabled>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {options.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </div>
        </Radio.Group>
      );
    case "checkbox":
      return <Checkbox disabled>{field.placeholder || field.label}</Checkbox>;
    default:
      return <Input {...common} />;
  }
};

/**
 * Form Builder -> ATS Application Form. Configures the public job application
 * form: show/hide and mandatory/optional for the built-in fields, plus
 * company-defined custom sections and fields. Like the other form builders every
 * change is saved as soon as it is made; with nothing saved the form is the
 * original built-in form.
 */
const AtsApplicationFormBuilder = () => {
  const canUpdate = getResource(config.RESOURCES.ATS, ["update"]);
  const [fieldForm] = Form.useForm();
  const [sectionForm] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [modalSaving, setModalSaving] = useState(false);
  const [catalog, setCatalog] = useState({ sections: [], builtInCatalog: [] });
  const [builtIn, setBuiltIn] = useState({});
  const [customSections, setCustomSections] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const [fieldModal, setFieldModal] = useState({ open: false, fieldName: null });
  const [sectionModal, setSectionModal] = useState({ open: false, key: null });
  const [watchedType, setWatchedType] = useState("text");

  // Latest form state, kept in a ref so rapid clicks always build on each other,
  // and a promise chain so saves reach the server in the order they were made.
  const stateRef = useRef({ builtIn: {}, customSections: [], customFields: [] });
  const saveQueueRef = useRef(Promise.resolve());
  const catalogRef = useRef([]);

  const applyServerData = (data) => {
    const next = { builtIn: data.builtIn || {}, customSections: data.customSections || [], customFields: data.customFields || [] };
    catalogRef.current = data.builtInCatalog || [];
    stateRef.current = next;
    setCatalog({ sections: data.sections || [], builtInCatalog: data.builtInCatalog || [] });
    setBuiltIn(next.builtIn);
    setCustomSections(next.customSections);
    setCustomFields(next.customFields);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Service.makeAPICall({ methodName: Service.getMethod, api_url: Service.atsApplicationForm });
      if (response?.data?.status === 200) {
        applyServerData(response.data.data);
      } else {
        message.error(response?.data?.message || "Could not load the application form");
      }
    } catch (error) {
      message.error("Could not load the application form");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persist = (snapshot) => {
    const builtInFields = catalogRef.current
      .filter((field) => !field.locked)
      .reduce((fields, field) => {
        fields[field.key] = { active: snapshot.builtIn[field.key]?.active !== false, required: snapshot.builtIn[field.key]?.required === true };
        return fields;
      }, {});
    return Service.makeAPICall({
      methodName: Service.putMethod,
      api_url: Service.atsApplicationForm,
      body: { builtInFields, customSections: snapshot.customSections, customFields: snapshot.customFields },
    });
  };

  /**
   * Applies a change to the form and saves it straight away, like the other form
   * builders. The screen updates immediately; if the save fails the saved
   * version is loaded back. Resolves to true when the save succeeded.
   */
  const commit = (updater, successMessage) => {
    const next = updater(stateRef.current);
    stateRef.current = next;
    setBuiltIn(next.builtIn);
    setCustomSections(next.customSections);
    setCustomFields(next.customFields);

    const run = async () => {
      try {
        const response = await persist(next);
        if (response?.data?.status === 200) {
          message.success(successMessage);
          return true;
        }
        message.error(response?.data?.message || "Could not save the change");
      } catch (error) {
        message.error("Could not save the change");
      }
      await load();
      return false;
    };
    const result = saveQueueRef.current.then(run);
    saveQueueRef.current = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  };

  const setBuiltInState = (key, patch) =>
    commit((current) => {
      const next = { ...(current.builtIn[key] || { active: true, required: false }), ...patch };
      if (next.active === false) next.required = false;
      return { ...current, builtIn: { ...current.builtIn, [key]: next } };
    }, "active" in patch ? (patch.active ? "Field shown in form" : "Field hidden from form") : patch.required ? "Field set to Mandatory" : "Field set to Optional");

  const patchField = (fieldName, patch) =>
    commit(
      (current) => ({
        ...current,
        customFields: current.customFields.map((field) => {
          if (field.fieldName !== fieldName) return field;
          const next = { ...field, ...patch };
          if (next.isActive === false) next.required = false;
          return next;
        }),
      }),
      "isActive" in patch ? (patch.isActive ? "Field shown in form" : "Field hidden from form") : patch.required ? "Field set to Mandatory" : "Field set to Optional"
    );

  const patchSection = (key, patch) =>
    commit(
      (current) => ({ ...current, customSections: current.customSections.map((section) => (section.key === key ? { ...section, ...patch } : section)) }),
      patch.isActive ? "Section shown in form" : "Section hidden from form"
    );

  const removeField = (fieldName) =>
    commit((current) => ({ ...current, customFields: current.customFields.filter((field) => field.fieldName !== fieldName) }), "Field deleted");

  const removeSection = (key) =>
    commit(
      (current) => ({
        ...current,
        customSections: current.customSections.filter((section) => section.key !== key),
        customFields: current.customFields.filter((field) => field.sectionKey !== key),
      }),
      "Section deleted"
    );

  // ---- drag and drop (custom sections and custom fields only; built-in order is fixed)
  const handleDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination || !canUpdate) return;
    if (type === "SECTION") {
      if (source.index === destination.index) return;
      commit((current) => {
        const sections = [...current.customSections];
        const [moved] = sections.splice(source.index, 1);
        sections.splice(destination.index, 0, moved);
        return { ...current, customSections: sections };
      }, "Section order updated");
      return;
    }
    const sourceKey = source.droppableId.replace("fields:", "");
    const destinationKey = destination.droppableId.replace("fields:", "");
    if (sourceKey === destinationKey && source.index === destination.index) return;
    commit((current) => {
      const moving = current.customFields.filter((field) => field.sectionKey === sourceKey)[source.index];
      if (!moving) return current;
      const others = current.customFields.filter((field) => field.fieldName !== moving.fieldName);
      const destinationList = others.filter((field) => field.sectionKey === destinationKey);
      destinationList.splice(destination.index, 0, { ...moving, sectionKey: destinationKey });
      return { ...current, customFields: [...others.filter((field) => field.sectionKey !== destinationKey), ...destinationList] };
    }, "Field order updated");
  };

  const allSections = [...catalog.sections.map((section) => ({ ...section, isCustom: false })), ...customSections.map((section) => ({ ...section, isCustom: true }))];

  // ---- modals
  const openFieldModal = (field = null) => {
    setFieldModal({ open: true, fieldName: field?.fieldName || null });
    setWatchedType(field?.fieldType || "text");
    fieldForm.resetFields();
    fieldForm.setFieldsValue(
      field
        ? { ...field }
        : { fieldLabel: "", fieldType: "text", sectionKey: allSections[0]?.key, placeholder: "", options: [], required: false }
    );
  };

  const submitFieldModal = async () => {
    const values = await fieldForm.validateFields();
    // Show/hide is the switch on the field card, not part of this form: keep it on edit, shown on add.
    const isActive = fieldModal.fieldName ? stateRef.current.customFields.find((field) => field.fieldName === fieldModal.fieldName)?.isActive !== false : true;
    const entry = {
      fieldName: fieldModal.fieldName || newId("cf_"),
      fieldLabel: values.fieldLabel.trim(),
      fieldType: values.fieldType,
      sectionKey: values.sectionKey,
      placeholder: (values.placeholder || "").trim(),
      options: OPTION_TYPES.includes(values.fieldType) ? values.options || [] : [],
      required: isActive && values.required === true,
      isActive,
    };
    setModalSaving(true);
    const saved = await commit(
      (current) => ({
        ...current,
        customFields: fieldModal.fieldName
          ? current.customFields.map((field) => (field.fieldName === entry.fieldName ? entry : field))
          : [...current.customFields, entry],
      }),
      fieldModal.fieldName ? "Field updated" : "Field added"
    );
    setModalSaving(false);
    if (saved) setFieldModal({ open: false, fieldName: null });
  };

  const openSectionModal = (section = null) => {
    setSectionModal({ open: true, key: section?.key || null });
    sectionForm.resetFields();
    sectionForm.setFieldsValue({ name: section?.name || "" });
  };

  const submitSectionModal = async () => {
    const { name } = await sectionForm.validateFields();
    const trimmed = name.trim();
    setModalSaving(true);
    const saved = await commit(
      (current) => ({
        ...current,
        customSections: sectionModal.key
          ? current.customSections.map((section) => (section.key === sectionModal.key ? { ...section, name: trimmed } : section))
          : [...current.customSections, { key: newId("cs_"), name: trimmed, isActive: true }],
      }),
      sectionModal.key ? "Section updated" : "Section added"
    );
    setModalSaving(false);
    if (saved) setSectionModal({ open: false, key: null });
  };

  // ---- field cards
  const renderCardShell = ({ label, required, controls, preview, radio, handle, key, style }) => (
    <div key={key} style={{ ...FIELD_CARD_STYLE, ...style }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, gap: 8, minWidth: 0 }}>
        <label style={{ fontWeight: 500, display: "flex", alignItems: "center", flex: 1, fontSize: 14, minWidth: 0 }}>
          {handle}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
          {required && <span style={{ color: "red", marginLeft: 4 }}>*</span>}
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>{controls}</div>
      </div>
      {preview}
      {radio}
    </div>
  );

  const renderBuiltInCard = (field) => {
    const state = field.locked ? { active: true, required: true } : builtIn[field.key] || { active: true, required: field.required };
    const active = state.active !== false;
    return renderCardShell({
      key: field.key,
      label: field.label,
      required: active && state.required === true,
      controls: (
        <>
          {field.locked ? (
            <Tooltip title="Always shown and mandatory - needed to create the candidate">
              <LockOutlined style={{ color: "#999" }} />
            </Tooltip>
          ) : null}
          <Switch
            size="small"
            checked={active}
            disabled={field.locked || !canUpdate}
            onChange={(checked) => setBuiltInState(field.key, { active: checked })}
            title={active ? "Shown in form (toggle to hide)" : "Hidden from form (toggle to show)"}
          />
        </>
      ),
      preview: renderPreview({ type: field.type, label: field.label }),
      radio: (
        <Radio.Group
          size="small"
          value={state.required === true}
          onChange={(event) => setBuiltInState(field.key, { required: event.target.value })}
          disabled={field.locked || !canUpdate || !active}
          style={{ marginTop: "auto", paddingTop: 8 }}
        >
          <Radio value={true}>Mandatory</Radio>
          <Radio value={false}>Optional</Radio>
        </Radio.Group>
      ),
    });
  };

  const renderCustomCard = (field, index) => (
    <Draggable key={field.fieldName} draggableId={field.fieldName} index={index} isDragDisabled={!canUpdate}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps} style={{ ...provided.draggableProps.style, ...FIELD_CARD_STYLE }}>
          <div
            style={{
              padding: snapshot.isDragging ? 12 : 0,
              background: snapshot.isDragging ? "#f5f7fa" : "transparent",
              borderRadius: 6,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              border: snapshot.isDragging ? "2px dashed #03497a" : "none",
              boxShadow: snapshot.isDragging ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
            }}
          >
            {renderCardShell({
              key: field.fieldName,
              label: field.fieldLabel,
              required: field.isActive !== false && field.required === true,
              style: { width: "100%", minWidth: 0, flex: 1 },
              handle: (
                <span
                  {...provided.dragHandleProps}
                  style={{ cursor: "grab", marginRight: 8, display: "inline-flex", alignItems: "center", padding: 4, borderRadius: 4, background: snapshot.isDragging ? "#03497a" : "transparent" }}
                >
                  <DragOutlined style={{ color: snapshot.isDragging ? "white" : "#999", fontSize: 14 }} />
                </span>
              ),
              controls: (
                <>
                  <Switch
                    size="small"
                    checked={field.isActive !== false}
                    disabled={!canUpdate}
                    onChange={(checked) => patchField(field.fieldName, { isActive: checked })}
                    title={field.isActive !== false ? "Shown in form (toggle to hide)" : "Hidden from form (toggle to show)"}
                  />
                  <Button type="text" size="small" icon={<EditOutlined />} title="Edit field" disabled={!canUpdate} onClick={() => openFieldModal(field)} />
                  <Popconfirm
                    title="Are you sure you want to delete this field?"
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                    zIndex={1100}
                    getPopupContainer={() => document.body}
                    disabled={!canUpdate}
                    onConfirm={() => removeField(field.fieldName)}
                  >
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} title="Delete field" disabled={!canUpdate} />
                  </Popconfirm>
                </>
              ),
              preview: renderPreview({ type: field.fieldType, placeholder: field.placeholder, options: field.options, label: field.fieldLabel }),
              radio: (
                <Radio.Group
                  size="small"
                  value={field.required === true}
                  onChange={(event) => patchField(field.fieldName, { required: event.target.value })}
                  disabled={!canUpdate || field.isActive === false}
                  style={{ marginTop: "auto", paddingTop: 8 }}
                >
                  <Radio value={true}>Mandatory</Radio>
                  <Radio value={false}>Optional</Radio>
                </Radio.Group>
              ),
            })}
          </div>
        </div>
      )}
    </Draggable>
  );

  const renderSectionBody = (section) => {
    const builtInCards = section.isCustom ? [] : catalog.builtInCatalog.filter((field) => field.sectionKey === section.key);
    const sectionFields = customFields.filter((field) => field.sectionKey === section.key);
    return (
      <>
        {builtInCards.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, width: "100%", marginBottom: sectionFields.length ? 24 : 0 }}>{builtInCards.map(renderBuiltInCard)}</div>
        ) : null}
        <Droppable droppableId={fieldsDroppableId(section.key)} type="FIELD">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ display: "flex", flexWrap: "wrap", gap: 24, width: "100%", minHeight: sectionFields.length || builtInCards.length ? "auto" : 80 }}
            >
              {!sectionFields.length && !builtInCards.length ? (
                <div style={{ width: "100%", textAlign: "center", padding: 24, color: "#999", background: "#fafafa", borderRadius: 6, border: "1px dashed #d9d9d9" }}>
                  No fields in this section. Add a field to get started.
                </div>
              ) : (
                sectionFields.map((field, index) => renderCustomCard(field, index))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </>
    );
  };

  const renderSectionHeader = (section, dragHandleProps, isDragging) => (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h3 style={SECTION_TITLE_STYLE}>
          {section.isCustom ? (
            <span {...dragHandleProps} style={{ cursor: "grab", display: "inline-flex", padding: 4 }}>
              <DragOutlined style={{ color: isDragging ? "#03497a" : "#999" }} />
            </span>
          ) : null}
          {section.name}
          {!section.isCustom ? <span style={MUTED_NOTE}>Default</span> : null}
          {section.isCustom ? (
            <Switch
              size="small"
              checked={section.isActive !== false}
              disabled={!canUpdate}
              onChange={(checked) => patchSection(section.key, { isActive: checked })}
              title={section.isActive !== false ? "Shown in form (toggle to hide)" : "Hidden from form (toggle to show)"}
            />
          ) : null}
        </h3>
        {section.isCustom ? (
          <div style={{ display: "flex", gap: 8 }}>
            <Button type="text" size="small" icon={<EditOutlined />} disabled={!canUpdate} onClick={() => openSectionModal(section)} />
            <Popconfirm
              placement="topLeft"
              title={
                <span>
                  Are you sure you want to delete the section "{section.name}" <br /> and all fields related to it?
                </span>
              }
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
              zIndex={1100}
              getPopupContainer={() => document.body}
              disabled={!canUpdate}
              onConfirm={() => removeSection(section.key)}
            >
              <Button type="text" danger size="small" icon={<DeleteOutlined />} disabled={!canUpdate} />
            </Popconfirm>
          </div>
        ) : null}
      </div>
      <Divider style={{ margin: "12px 0 24px 0" }} />
    </>
  );

  return (
    <Card className="employee-card addleave-btn">
      <div className=" inout-header text-left">
        <h2>ATS Application Form Builder</h2>
        {canUpdate ? (
          <div className="header-btns">
            <Button type="primary" onClick={() => openSectionModal()} className="add-btn" icon={<PlusOutlined />}>
              Add Section
            </Button>
            <Button type="primary" onClick={() => openFieldModal()} icon={<PlusOutlined />}>
              Add Custom Field
            </Button>
          </div>
        ) : null}
      </div>

      <Card className="main-content-wrapper">
        <LoadingOverlay loading={loading}>
          <div style={{ padding: "24px 0", width: "100%" }}>
            <DragDropContext onDragEnd={handleDragEnd}>
              {catalog.sections.map((section) => (
                <div key={section.key} style={{ marginBottom: 32 }}>
                  {renderSectionHeader({ ...section, isCustom: false })}
                  {renderSectionBody({ ...section, isCustom: false })}
                </div>
              ))}

              <Droppable droppableId="custom-sections" type="SECTION">
                {(sectionsProvided) => (
                  <div {...sectionsProvided.droppableProps} ref={sectionsProvided.innerRef}>
                    {customSections.map((section, index) => (
                      <Draggable key={section.key} draggableId={section.key} index={index} isDragDisabled={!canUpdate}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              marginBottom: 32,
                              padding: snapshot.isDragging ? 16 : 0,
                              background: snapshot.isDragging ? "#f5f7fa" : "transparent",
                              borderRadius: 8,
                              border: snapshot.isDragging ? "2px dashed #03497a" : "none",
                            }}
                          >
                            {renderSectionHeader({ ...section, isCustom: true }, provided.dragHandleProps, snapshot.isDragging)}
                            {renderSectionBody({ ...section, isCustom: true })}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {sectionsProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </LoadingOverlay>
      </Card>

      <Modal
        title={fieldModal.fieldName ? "Edit Custom Field" : "Add Custom Field"}
        open={fieldModal.open}
        onCancel={() => setFieldModal({ open: false, fieldName: null })}
        width={520}
        forceRender
        footer={[
          <Button key="cancel" onClick={() => setFieldModal({ open: false, fieldName: null })}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={modalSaving} onClick={submitFieldModal}>
            {fieldModal.fieldName ? "Update Field" : "Add Field"}
          </Button>,
        ]}
      >
        <Form form={fieldForm} layout="vertical" onValuesChange={(changedValues) => changedValues.fieldType && setWatchedType(changedValues.fieldType)}>
          <Form.Item label="Field Label" name="fieldLabel" rules={[{ required: true, whitespace: true, message: "Please enter field label!" }, { max: 120 }]}>
            <Input placeholder="e.g., Why do you want to join us?" />
          </Form.Item>
          <Form.Item label="Section" name="sectionKey" rules={[{ required: true, message: "Please select section!" }]}>
            <Select placeholder="Select section" options={allSections.map((section) => ({ value: section.key, label: section.name }))} />
          </Form.Item>
          <Form.Item label="Field Type" name="fieldType" rules={[{ required: true, message: "Please select field type!" }]}>
            <Select placeholder="Select field type" options={Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
          </Form.Item>
          {OPTION_TYPES.includes(watchedType) ? (
            <Form.Item
              label="Options"
              name="options"
              rules={[{ required: true, type: "array", min: 1, message: "Add at least one option" }]}
              extra="Type an option and press Enter."
            >
              <Select mode="tags" tokenSeparators={[","]} open={false} placeholder="e.g., Immediately" />
            </Form.Item>
          ) : null}
          <Form.Item label={watchedType === "checkbox" ? "Checkbox text" : "Placeholder"} name="placeholder" rules={[{ max: 120 }]}>
            <Input placeholder={watchedType === "checkbox" ? "e.g., I am willing to relocate" : "Optional hint shown inside the field"} />
          </Form.Item>
          <Form.Item label="Field Requirement" name="required">
            <Radio.Group>
              <Radio value={true}>Mandatory</Radio>
              <Radio value={false}>Optional</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={sectionModal.key ? "Edit Section" : "Add New Section"}
        open={sectionModal.open}
        onCancel={() => setSectionModal({ open: false, key: null })}
        forceRender
        footer={[
          <Button key="cancel" onClick={() => setSectionModal({ open: false, key: null })}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={modalSaving} onClick={submitSectionModal}>
            {sectionModal.key ? "Update Section" : "Add Section"}
          </Button>,
        ]}
      >
        <Form form={sectionForm} layout="vertical">
          <Form.Item label="Section Name" name="name" rules={[{ required: true, whitespace: true, message: "Please enter section name!" }, { max: 60 }]}>
            <Input placeholder="e.g., Additional Questions" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AtsApplicationFormBuilder;
